// frontend/src/fetchInterceptor.js

import { getMsalInstance } from "./utils/msalInstance";
import { loginRequest } from "./authConfig";
import { recordAuthDiag } from "./utils/authDiag";

// Prevent double install in dev/HMR
if (!window.__FETCH_INTERCEPTOR_INSTALLED__) {
  window.__FETCH_INTERCEPTOR_INSTALLED__ = true;

  const originalFetch = window.fetch;

  // One-shot silent token refresh, de-duplicated across concurrent 401s.
  // A stale/expired access token is the usual cause of a 401 right after loading
  // the app with a leftover session; refreshing it silently (MSAL uses the cached
  // refresh token — no interactive redirect) lets the original request be retried
  // instead of tearing down the session and forcing a second sign-in.
  let _refreshPromise = null;
  async function trySilentTokenRefresh() {
    const msal = getMsalInstance();
    if (!msal) return null;
    let account;
    try {
      account = msal.getAllAccounts()[0];
    } catch {
      return null;
    }
    if (!account) return null;
    if (!_refreshPromise) {
      _refreshPromise = msal
        .acquireTokenSilent({ ...loginRequest, account })
        .then((res) => {
          const t = res && res.accessToken;
          if (t) {
            try { localStorage.setItem("token", t); } catch { /* no-op */ }
          }
          return t || null;
        })
        .catch((err) => { recordAuthDiag('interceptor.silentRefresh', err); return null; }) // InteractionRequired / no session → give up, caller redirects
        .finally(() => { _refreshPromise = null; });
    }
    return _refreshPromise;
  }

  // Backend base URL from .env
  const API_BASE = (process.env.REACT_APP_API_BASE_URL || "").replace(/\/$/, "");
  const API_ORIGIN = API_BASE ? new URL(API_BASE).origin : "";

  // Public (unauthenticated) backend endpoints
  const PUBLIC_BACKEND_PATHS = [
    "/api/ekyc/verify-token",
    "/api/ekyc/create-session",
    "/api/ekyc/download-tac",
    "/api/client-ekyc/verify-token",
    "/api/client-ekyc/create-session",
    "/api/client-ekyc/download-form",
    "/api/client-ekyc/form-description",
    "/api/client-ekyc/upload-signed-form",
    "/api/email-change/verify",
    "/api/public-market-reports",
    "/api/managed-portfolio-factsheets",
    "/api/bookings",
    "/api/demo/self-register",
    "/health",
  ];

  function toAbsoluteURL(input) {
    if (typeof input === "string") return new URL(input, window.location.origin);
    return new URL(input.url, window.location.origin);
  }

  function isBackendURL(absUrl) {
    return API_ORIGIN && absUrl.origin === API_ORIGIN;
  }

  function isPublicBackendPath(absUrl) {
    if (!isBackendURL(absUrl)) return false;
    return PUBLIC_BACKEND_PATHS.some((p) => absUrl.pathname.startsWith(p));
  }

  window.fetch = async (input, options = {}) => {
    const absUrl = toAbsoluteURL(input);
    const headers = new Headers(options.headers || {});
    const hasBody = !!options.body;
    const isMultipart =
      typeof FormData !== "undefined" && options.body instanceof FormData;

    // Determine whether to attach Authorization
    const backendCall = isBackendURL(absUrl);
    const publicBackendCall = isPublicBackendPath(absUrl);

    // Auto-set content-type for JSON requests (except multipart)
    if (!isMultipart && hasBody && !headers.has("Content-Type")) {
      headers.set("Content-Type", "application/json");
    }

    // Attach Azure Entra token for protected backend requests
    if (backendCall && !publicBackendCall) {
      try {
        const token = localStorage.getItem("token");
        if (token) headers.set("Authorization", `Bearer ${token}`);
      } catch {
        // no-op
      }
    } else {
      // Ensure no token sent to public or third-party endpoints
      headers.delete("Authorization");
      headers.delete("X-MFA-Token");
    }

    let resp = await originalFetch(input, { ...options, headers });

    // Handle unauthorized responses (401).
    // First, self-heal: the access token has most likely just expired (e.g. the
    // app loaded with a leftover session). Try ONE silent refresh and retry the
    // request. If the MSAL session is still valid this succeeds transparently and
    // the user never sees a second sign-in. Only if the refresh is unavailable
    // (or the retry is still 401) do we clear the session and let React redirect.
    if (resp.status === 401 && backendCall && !publicBackendCall) {
      const freshToken = await trySilentTokenRefresh();
      if (freshToken) {
        headers.set("Authorization", `Bearer ${freshToken}`);
        resp = await originalFetch(input, { ...options, headers });
      }
    }

    // Clear token and let React components (ProtectedRoute, HomeRedirect) handle the redirect.
    // Do NOT use window.location.href — it causes full page reloads that race with React state.
    if (resp.status === 401 && backendCall && !publicBackendCall) {
      localStorage.removeItem("token");
      localStorage.removeItem("userRole");
      localStorage.removeItem("userData");
      try {
        const body = await resp.clone().json();
        const code = body?.errorCode || "";
        if (code === "TOKEN_EXPIRED") {
          window.dispatchEvent(new CustomEvent('app-toast', { detail: { type: 'error', message: 'Your session has expired. Please log in again.' } }));
        } else if (code === "INVALID_TOKEN") {
          window.dispatchEvent(new CustomEvent('app-toast', { detail: { type: 'error', message: 'Invalid session. Please log in again.' } }));
        }
      } catch {
        // ignore parse errors
      }
    }

    return resp;
  };
}
